import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FaSearch } from 'react-icons/fa';

import StatusBadge from '../../../components/StatusBadge';
import { SmallButton } from '../../../styles/ui/Button';
import {
   AMBER,
   BLUE,
   SearchBox,
   SearchInput,
   SearchToolbar,
   ToolbarMeta,
} from '../styles';

// ---------------------------------------------------------------------------
// Aba "Usuários (n)" — listagem dos usuários vinculados ao papel, via
// `GET /users?role_id=X&status=all` (Gen3, handoff 0110).
//
// Adaptações CONSCIENTES sobre o protótipo (decisão do Caio, 2026-09-15):
//   · sem colunas Filial e Último acesso — o backend não expõe esses dados
//     na listagem (last_login não existe; PDVs só no detalhe);
//   · sem "Vincular usuário"/"Desvincular" — não há endpoint; o vínculo se
//     troca na ficha do usuário (role é atributo da conta);
//   · a busca é CLIENT-SIDE sobre a lista já carregada (teto de 100 do
//     contrato) — nada de auto-query na API enquanto digita.
// ---------------------------------------------------------------------------

const TableHead = styled.div`
   display: grid;
   grid-template-columns: minmax(280px, 1.4fr) 160px 150px 140px;
   align-items: center;
   height: 42px;
   padding: 0 18px;
   background: #373737;

   span {
      font-size: 11px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #fff;
   }

   span:last-child {
      text-align: right;
   }
`;

const RowLine = styled.div`
   display: grid;
   grid-template-columns: minmax(280px, 1.4fr) 160px 150px 140px;
   align-items: center;
   padding: 11px 18px;
   border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};

   &:hover {
      background: ${BLUE.bgHover};
   }
`;

const UserCell = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   min-width: 0;
`;

const Avatar = styled.span`
   flex: 0 0 auto;
   width: 32px;
   height: 32px;
   border-radius: 999px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   background: ${BLUE.bgActive};
   color: ${BLUE.text};
   font-size: 11.5px;
   font-weight: 800;
`;

const UserText = styled.div`
   min-width: 0;

   strong {
      display: block;
      font-size: 13.5px;
      font-weight: 700;
      color: #222;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
   small {
      display: block;
      font-size: 11.5px;
      color: #777;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
   }
`;

const DateCell = styled.div`
   font-size: 12.5px;
   color: #444;
   font-variant-numeric: tabular-nums;
`;

const ActionCell = styled.div`
   text-align: right;
`;

const Notice = styled.div`
   margin: 18px;
   padding: 12px 14px;
   border-radius: 10px;
   background: ${AMBER.bgSoft};
   border: 1px solid ${AMBER.borderSoft};
   font-size: 12.5px;
   color: ${AMBER.text};
`;

const Empty = styled.div`
   padding: 48px 18px;
   text-align: center;

   h4 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: #444;
   }
   p {
      margin: 6px 0 0;
      font-size: 12.5px;
      color: #777;
   }
`;

function initials(name) {
   const parts = String(name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
   if (!parts.length) return '?';
   const first = parts[0][0];
   const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
   return `${first}${last}`.toUpperCase();
}

function formatDate(value) {
   if (!value) return '—';
   const date = new Date(value);
   if (Number.isNaN(date.getTime())) return '—';
   return date.toLocaleDateString('pt-BR');
}

export default function UsersTab({
   users,
   total,
   loading,
   forbidden,
   query,
   onQueryChange,
   onOpenUser,
}) {
   const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return users;
      return users.filter(
         (user) =>
            String(user.nome ?? '').toLowerCase().includes(q) ||
            String(user.email ?? '').toLowerCase().includes(q)
      );
   }, [users, query]);

   const activeCount = users.filter((user) => user.is_active !== false).length;

   if (forbidden) {
      return (
         <Notice>
            Você não tem permissão para listar os usuários deste perfil
            (<code>iam:user:read</code>). As permissões continuam editáveis na
            aba ao lado.
         </Notice>
      );
   }

   return (
      <div>
         <SearchToolbar>
            <SearchBox>
               <FaSearch />
               <SearchInput
                  type="text"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  placeholder="Buscar usuário por nome ou e-mail"
               />
            </SearchBox>
            <ToolbarMeta>
               {total} vinculados · {activeCount} ativos
            </ToolbarMeta>
         </SearchToolbar>

         <TableHead>
            <span>Usuário</span>
            <span>Criado em</span>
            <span>Situação</span>
            <span>Ações</span>
         </TableHead>

         {loading && (
            <Empty>
               <p>Carregando usuários...</p>
            </Empty>
         )}

         {!loading && filtered.length === 0 && (
            <Empty>
               <h4>
                  {users.length === 0
                     ? 'Nenhum usuário vinculado'
                     : 'Nenhum usuário encontrado'}
               </h4>
               <p>
                  {users.length === 0
                     ? 'Não há contas de acesso usando este perfil.'
                     : 'Ajuste a busca.'}
               </p>
            </Empty>
         )}

         {!loading &&
            filtered.map((user) => (
               <RowLine key={String(user.id)}>
                  <UserCell>
                     <Avatar>{initials(user.nome)}</Avatar>
                     <UserText>
                        <strong>{user.nome}</strong>
                        <small>{user.email}</small>
                     </UserText>
                  </UserCell>
                  <DateCell>{formatDate(user.created_at)}</DateCell>
                  <div>
                     {user.is_active === false ? (
                        <StatusBadge variant="red">Excluído</StatusBadge>
                     ) : (
                        <StatusBadge variant="green">Ativo</StatusBadge>
                     )}
                  </div>
                  <ActionCell>
                     <SmallButton
                        type="button"
                        onClick={() => onOpenUser(user.id)}
                     >
                        Abrir ficha
                     </SmallButton>
                  </ActionCell>
               </RowLine>
            ))}
      </div>
   );
}

UsersTab.propTypes = {
   users: PropTypes.arrayOf(PropTypes.object).isRequired,
   total: PropTypes.number.isRequired,
   loading: PropTypes.bool,
   forbidden: PropTypes.bool,
   query: PropTypes.string,
   onQueryChange: PropTypes.func.isRequired,
   onOpenUser: PropTypes.func.isRequired,
};

UsersTab.defaultProps = {
   loading: false,
   forbidden: false,
   query: '',
};
